import NextImage from 'next/image'
import { useMemo, useState } from 'react'
import { isAfter, isBefore, isEqual, parse as parseDate } from 'date-fns'

import { Button } from '@wartek-id/button-v1'
import { Card } from '@wartek-id/card'
import { Text } from '@wartek-id/text-v1'
import { Banner, BannerDescription } from '@wartek-id/banner'
import { useToast } from '@wartek-id/toast'

import usePeriodContext from 'app/KinerjaShared/hooks/usePeriodContext'
import DialogConfirmStartPeriod from './DialogConfirmStartNewPeriod'
import { formatPeriod } from 'app/KinerjaShared/utils/period'
import { useAPIPostApprovePeriod } from 'api/kinerja/useAPIKinerja'
import useRoleContext from 'app/KinerjaShared/hooks/useRoleContext'
import useToggleSdkContext from 'utils/useToogleSdk'

import type { PelaksanaanSchool } from 'types/kinerja-pelaksanaan'

interface CardKSStartPeriodProps {
  isMultipleSchoolEnabled: boolean
  schools: PelaksanaanSchool[]
}

export default function CardKSStartPeriod({
  isMultipleSchoolEnabled,
  schools,
}: CardKSStartPeriodProps) {
  const toast = useToast()
  const {
    isEnabled: isMultiplePeriodEnabled,
    period: selectedPeriod,

    periods,
  } = usePeriodContext()
  const { mutateAsync, isLoading: isApproving } = useAPIPostApprovePeriod()
  const { profile } = useRoleContext()

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [showSuccessBanner, setShowSuccessBanner] = useState(false)

  const toggleValue = useToggleSdkContext()

  const isPeriodApprovalUseSelectedPeriodEnabled =
    toggleValue?.['kinerja-period-approval-use-selected-period'] || false

  const today = useMemo(() => new Date(), [])

  const currentPeriod = useMemo(() => {
    // bypass date checking so we could approve any period
    if (isPeriodApprovalUseSelectedPeriodEnabled) return selectedPeriod
    return periods?.find((period) => {
      const startDate = parseDate(period.startDate, 'yyyy-MM-dd', new Date())
      const endDate = parseDate(period.endDate, 'yyyy-MM-dd', new Date())

      // we need to check if today is in current period's range, so users can't approve future period
      return (
        (isBefore(today, endDate) || isEqual(today, endDate)) &&
        (isAfter(today, startDate) || isEqual(today, startDate))
      )
    })
  }, [isPeriodApprovalUseSelectedPeriodEnabled, selectedPeriod, periods, today])

  const canStartNewPeriod = useMemo(() => {
    if (isPeriodApprovalUseSelectedPeriodEnabled) return true
    if (currentPeriod) {
      const startPeriodDate = parseDate(
        currentPeriod.startDate,
        'yyyy-MM-dd',
        new Date()
      )

      return isAfter(today, startPeriodDate) || isEqual(today, startPeriodDate)
    }

    return false
  }, [currentPeriod, today, isPeriodApprovalUseSelectedPeriodEnabled])

  const formattedPeriod = useMemo(() => {
    if (currentPeriod) {
      return formatPeriod(currentPeriod.startDate, currentPeriod.endDate)
    }

    return ''
  }, [currentPeriod])

  const onConfirm = async () => {
    if (currentPeriod) {
      try {
        if (isMultipleSchoolEnabled) {
          const confirmedNpsn = schools
            .filter((school) => school.confirmationStatus === 'CONFIRMED')
            .map(({ npsn }) => npsn)

          // If there is no confirmed school, show error toast and return
          if (confirmedNpsn.length === 0) {
            toast({
              message:
                'Gagal memulai periode. Pastikan Satuan Pendidikan Anda sudah dikonfirmasi.',
            })
            return
          }

          await Promise.all(
            schools
              .filter((school) => school.confirmationStatus === 'CONFIRMED')
              .map(({ npsn }) =>
                mutateAsync({
                  npsn,
                  is_approved: true,
                  periode_id: currentPeriod?.id,
                })
              )
          )

          setShowSuccessBanner(true)
        } else if (profile) {
          await mutateAsync({
            npsn: profile.npsn,
            is_approved: true,
            periode_id: currentPeriod?.id,
          })

          setShowSuccessBanner(true)
        }
      } catch (e) {
        toast({
          message: 'Gagal memulai periode.',
        })
        console.error(e)
      } finally {
        setIsDialogOpen(false)
      }
    }
  }

  if (!isMultiplePeriodEnabled || !canStartNewPeriod) {
    return null
  }

  return (
    <>
      {/* When the period has not yet been started (required actions from KS) */}
      {!currentPeriod.isApproved && !showSuccessBanner && (
        <Card>
          <div className="flex gap-5 p-3">
            <NextImage
              src="/icon/menu-pengelolaan-kinerja.svg"
              alt=""
              width={84}
              height={84}
            />

            <div className="flex flex-col gap-3">
              <div>
                <Text variant="body-bold">
                  Mulai periode {formattedPeriod} di satuan pendidikan Anda
                </Text>
                <Text color="subdued">
                  Mari mulai Pengelolaan Kinerja periode berikutnya. Tenang,
                  periode sebelumnya tetap dapat diakses.
                </Text>
              </div>

              <div>
                <Button
                  size="sm"
                  onClick={() => {
                    setIsDialogOpen(true)
                  }}
                >
                  Mulai periode
                </Button>
              </div>
            </div>
          </div>
        </Card>
      )}

      <DialogConfirmStartPeriod
        currentPeriod={formattedPeriod}
        isLoading={isApproving}
        isOpen={isDialogOpen}
        onClose={() => setIsDialogOpen(false)}
        onConfirm={onConfirm}
      />

      {/* When the period had been successfully started */}
      {showSuccessBanner && (
        <Banner
          title={`Periode ${formattedPeriod} sudah dimulai`}
          type="success"
        >
          <BannerDescription>
            <Text>
              Kami sudah kabari guru di satuan pendidikan Anda serta Tim Kerja
              (Pengawas Sekolah) dan Dinas Pendidikan. Anda juga dapat ganti
              periode untuk akses periode sebelumnya.
            </Text>
          </BannerDescription>
        </Banner>
      )}
    </>
  )
}
